/* Demo orchestrator.
 *
 * Phase loop is ASYNC: each phase shows its visuals, kicks off narration,
 * and AWAITS completion before advancing. Narration source:
 *
 *   1. PRIMARY  — MP3 file in /demo/audio/{phase}-{lang}.mp3 (real TTS-1-HD
 *                 voice quality, human-grade). Generated via
 *                 `npm run gen:demo-audio` with an OPENAI_API_KEY.
 *
 *   2. FALLBACK — browser Web Speech (less robotic now, but still TTS) if
 *                 the MP3 doesn't exist. So the demo works even without
 *                 running the generation script first.
 *
 * Phase advance = narration end (with a 4.5s minimum dwell so visuals
 * have time to land). No more fixed-duration timers cutting off voice.
 */

import '../styles/demo-page.css';
import { Scene } from './scene';
import { PHASES, type Phase } from './phases';
import { showScreen } from './screens';
import { demoAudio, AudioMissing } from './audio';
import { voice, type Lang } from './voice';

const LANG_STORAGE_KEY = 'lp_aiboard_lang';
const SPLASH_ID = 'demo-splash';
const SCREEN_HOST_ID = 'demo-canvas-overlay';
const PHASE_TAIL_MS = 350;       // brief pause between phases
const MIN_PHASE_MS = 4500;       // floor — visuals are readable even on muted runs

let lang: Lang = pickInitialLang();
let scene: Scene | null = null;
let phaseIdx = 0;
let paused = false;
let muted = false;
let started = false;
let ended = false;
/** Bumped on every skip / lang-switch / cancel. In-flight awaits compare
 *  their captured runId vs the global one and bail if they differ. */
let runId = 0;

function pickInitialLang(): Lang {
  const url = new URL(location.href);
  const q = url.searchParams.get('lang');
  if (q === 'es' || q === 'pt' || q === 'en') return q;
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === 'es' || stored === 'pt' || stored === 'en') return stored;
  } catch {}
  return 'en';
}

function $<T extends HTMLElement = HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function wait(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

function setLangButtons(): void {
  document.querySelectorAll<HTMLButtonElement>('.demo-lang').forEach((b) => {
    b.classList.toggle('active', b.dataset.lang === lang);
  });
}

function renderProgress(): void {
  const el = $('demo-progress');
  if (!el) return;
  el.innerHTML = '';
  PHASES.forEach((_, i) => {
    const dot = document.createElement('span');
    dot.className = 'demo-tick';
    if (i < phaseIdx) dot.classList.add('demo-tick-done');
    if (i === phaseIdx) dot.classList.add('demo-tick-active');
    el.appendChild(dot);
  });
}

function applyPhase(phase: Phase): void {
  const badge = $('demo-badge');
  const caption = $('demo-caption');
  const sub = $('demo-subcaption');
  if (badge)   badge.textContent = phase.badge[lang];
  if (caption) caption.textContent = phase.caption[lang];
  if (sub)     sub.textContent = phase.subcaption[lang];
  [badge, caption, sub].forEach((el) => {
    if (!el) return;
    el.classList.remove('demo-anim-in');
    void el.offsetWidth;
    el.classList.add('demo-anim-in');
  });
  scene?.setScene(phase.scene);
  const host = $(SCREEN_HOST_ID);
  if (host) showScreen(phase.screen, host, lang);
  renderProgress();
}

async function narrate(phase: Phase, myRunId: number): Promise<void> {
  if (muted) {
    await wait(MIN_PHASE_MS);
    return;
  }
  try {
    await demoAudio.play(phase.screen, lang);
    return;
  } catch (err) {
    if (myRunId !== runId) return;
    if (!(err instanceof AudioMissing)) {
      console.warn('demo audio failed; using browser TTS fallback', err);
    }
    await voice.speak(phase.voice[lang]);
  }
}

async function runPhases(): Promise<void> {
  const myRunId = ++runId;
  for (let i = phaseIdx; i < PHASES.length; i++) {
    if (myRunId !== runId || ended) return;
    phaseIdx = i;
    const phase = PHASES[i];
    applyPhase(phase);
    // Run narration + minimum dwell in parallel, await the slower of the two
    const dwell = wait(MIN_PHASE_MS);
    const narration = narrate(phase, myRunId);
    await Promise.all([dwell, narration]);
    if (myRunId !== runId || ended) return;
    await wait(PHASE_TAIL_MS);
  }
  if (myRunId === runId && !ended) showEnd();
}

function startShow(): void {
  if (started) return;
  started = true;
  ended = false;
  phaseIdx = 0;
  paused = false;
  const splash = $(SPLASH_ID);
  if (splash) splash.classList.add('demo-splash-hidden');
  $('demo-main')?.removeAttribute('aria-hidden');
  $('demo-controls')?.removeAttribute('aria-hidden');
  $('demo-bottombar')?.removeAttribute('aria-hidden');
  $('demo-main')?.classList.add('demo-running');

  voice.setLang(lang);
  voice.setMuted(muted);
  demoAudio.setMuted(muted);

  runPhases();
}

function showEnd(): void {
  ended = true;
  runId++;
  voice.cancel();
  demoAudio.stop();
  const tpl = document.getElementById('demo-end-template') as HTMLTemplateElement | null;
  const phaseRoot = $('demo-phase');
  if (!tpl || !phaseRoot) return;
  document.querySelectorAll('.demo-tick').forEach((el) => {
    el.classList.add('demo-tick-done');
    el.classList.remove('demo-tick-active');
  });
  phaseRoot.innerHTML = '';
  phaseRoot.appendChild(tpl.content.cloneNode(true));
  document.getElementById('demo-replay-btn')?.addEventListener('click', () => location.reload());
  scene?.setScene('matrix');
}

function togglePause(): void {
  if (ended) return;
  paused = !paused;
  scene?.setPaused(paused);
  const btn = document.querySelector('[data-action="pause"]');
  btn?.classList.toggle('demo-ctrl-active', paused);
  if (paused) {
    demoAudio.pause();
    voice.pause();
  } else {
    demoAudio.resume();
    voice.resume();
  }
}

function toggleMute(): void {
  muted = !muted;
  demoAudio.setMuted(muted);
  voice.setMuted(muted);
  const btn = document.querySelector('[data-action="mute"]');
  btn?.classList.toggle('demo-ctrl-active', muted);
}

function skipToEnd(): void {
  if (!started) {
    startShow();
    setTimeout(showEnd, 50);
    return;
  }
  if (ended) return;
  runId++;
  demoAudio.stop();
  voice.cancel();
  showEnd();
}

function skipPhase(): void {
  if (!started || ended) return;
  runId++;
  demoAudio.stop();
  voice.cancel();
  phaseIdx += 1;
  if (phaseIdx >= PHASES.length) {
    showEnd();
  } else {
    runPhases();
  }
}

function wireControls(): void {
  document.querySelector('[data-action="pause"]')?.addEventListener('click', togglePause);
  document.querySelector('[data-action="mute"]')?.addEventListener('click', toggleMute);
  document.querySelector('[data-action="skip"]')?.addEventListener('click', skipToEnd);

  document.querySelectorAll<HTMLButtonElement>('.demo-lang').forEach((b) => {
    b.addEventListener('click', () => {
      const next = (b.dataset.lang as Lang) || 'en';
      if (next === lang) return;
      lang = next;
      try { localStorage.setItem(LANG_STORAGE_KEY, lang); } catch {}
      voice.setLang(lang);
      setLangButtons();
      if (!started) {
        setSplashCopy();
      } else if (!ended) {
        runId++;
        demoAudio.stop();
        voice.cancel();
        runPhases();
      }
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === ' ' && started && !ended) { e.preventDefault(); togglePause(); }
    else if (e.key.toLowerCase() === 'm') toggleMute();
    else if (e.key === 'ArrowRight' && started && !ended) skipPhase();
    else if (e.key === 'Escape') skipToEnd();
    else if (e.key === 'Enter' && !started) startShow();
  });
}

function setSplashCopy(): void {
  const eyebrow = document.querySelector('.demo-splash-eyebrow');
  const sub = document.querySelector('.demo-splash-sub');
  const cta = document.querySelector('#demo-start-btn span');
  const meta = document.querySelector('.demo-splash-meta span');
  const titles = document.querySelectorAll<HTMLElement>('.demo-splash-title');
  if (lang === 'es') {
    if (eyebrow) eyebrow.textContent = 'TOUR DE 60 SEGUNDOS';
    if (sub) sub.textContent = 'Un prompt a cinco modelos. Compara, sintetiza, guarda el prompt reutilizable. Tus llaves nunca salen del navegador.';
    if (cta) cta.textContent = 'Ver la demo';
    if (meta) meta.textContent = '~60 segundos · voz · sáltala cuando quieras';
    if (titles[0]) titles[0].textContent = 'Cinco mentes.';
    if (titles[1]) titles[1].textContent = 'Una respuesta.';
  } else if (lang === 'pt') {
    if (eyebrow) eyebrow.textContent = 'TOUR DE 60 SEGUNDOS';
    if (sub) sub.textContent = 'Um prompt para cinco modelos. Compare, sintetize, salve o prompt reutilizável. Suas chaves nunca saem do navegador.';
    if (cta) cta.textContent = 'Ver a demo';
    if (meta) meta.textContent = '~60 segundos · voz · pule quando quiser';
    if (titles[0]) titles[0].textContent = 'Cinco mentes.';
    if (titles[1]) titles[1].textContent = 'Uma resposta.';
  } else {
    if (eyebrow) eyebrow.textContent = '60-SECOND TOUR';
    if (sub) sub.textContent = "One prompt to five models. Compare, synthesize, save the reusable prompt. Your keys never leave your browser.";
    if (cta) cta.textContent = 'Watch the demo';
    if (meta) meta.textContent = '~60 seconds · voice · skip anytime';
    if (titles[0]) titles[0].textContent = 'Five minds.';
    if (titles[1]) titles[1].textContent = 'One answer.';
  }
}

function init(): void {
  setLangButtons();
  setSplashCopy();
  voice.setLang(lang);

  const canvas = $('demo-stage') as HTMLCanvasElement | null;
  if (canvas) {
    try {
      scene = new Scene(canvas);
      scene.start();
    } catch (err) {
      console.warn('demo scene failed to start', err);
    }
  }

  wireControls();
  $('demo-start-btn')?.addEventListener('click', startShow);
  $(SPLASH_ID)?.addEventListener('click', (e) => {
    if (e.target instanceof HTMLButtonElement) return;
    startShow();
  });
}

document.addEventListener('DOMContentLoaded', init);
